import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './PurchaseBarcodePrint.css';

const apiBaseURL = (process.env.REACT_APP_API_BASE_URL || 'https://api.vehiclesolution.co').replace(/\/+$/, '');

const CODE128_PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];

const getCode128Values = (text) => {
  const safeText = String(text || '').replace(/[^\x20-\x7e]/g, '').slice(0, 48) || '0';
  const values = [104];
  let checksum = 104;

  for (let index = 0; index < safeText.length; index += 1) {
    const value = safeText.charCodeAt(index) - 32;
    values.push(value);
    checksum += value * (index + 1);
  }

  values.push(checksum % 103);
  values.push(106);
  return values;
};

const PRICE_CODE_PREFIXES = {
  cost: 'PP',
  sale: 'SP',
};

const PRICE_CODE_MAP = {
  1: 'M',
  2: 'N',
  3: 'O',
  4: 'P',
  5: 'Q',
  6: 'A',
  7: 'B',
  8: 'C',
  9: 'D',
  0: '#',
  '.': '@',
};

const encodePrice = (value, type) => {
  const prefix = PRICE_CODE_PREFIXES[type] || '';
  const numberValue = Number(value || 0);
  const text = Number.isFinite(numberValue) ? numberValue.toFixed(2) : '0.00';
  const encoded = text
    .split('')
    .map((character) => PRICE_CODE_MAP[character] || '')
    .join('');

  return `${prefix}${encoded}`;
};

function BarcodeSvg({ value }) {
  const bars = useMemo(() => {
    const values = getCode128Values(value);
    let x = 0;
    const rects = [];

    values.forEach((codeValue, codeIndex) => {
      const pattern = CODE128_PATTERNS[codeValue] || CODE128_PATTERNS[0];
      pattern.split('').forEach((widthText, widthIndex) => {
        const width = Number(widthText);
        if (widthIndex % 2 === 0) {
          rects.push({ x, width, key: `${codeIndex}-${widthIndex}` });
        }
        x += width;
      });
    });

    return { rects, width: x };
  }, [value]);

  return (
    <svg className="barcode-svg" viewBox={`0 0 ${bars.width} 46`} preserveAspectRatio="none" aria-label={`Barcode ${value}`}>
      {bars.rects.map((rect) => (
        <rect key={rect.key} x={rect.x} y="0" width={rect.width} height="46" />
      ))}
    </svg>
  );
}

function PurchaseBarcodePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadPurchase = async () => {
      setLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${apiBaseURL}/api/purchase/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to load purchase item');
        }

        setPurchase(data.purchase);
      } catch (fetchError) {
        setError(fetchError.message || 'Failed to load purchase item');
      } finally {
        setLoading(false);
      }
    };

    loadPurchase();
  }, [id]);

  const labels = useMemo(() => {
    const qty = Math.max(1, Math.floor(Number(purchase?.qty || 1)));
    return Array.from({ length: qty }, (_, index) => index);
  }, [purchase]);

  const barcodeValue = purchase?.tx_no || purchase?.grn_no || purchase?.part_no || String(purchase?.purchase_id || '');

  return (
    <main className="barcode-print-page">
      <div className="barcode-print-actions">
        <button type="button" onClick={() => navigate('/inventory/purchases')}>Back</button>
        <button type="button" onClick={() => window.print()} disabled={!purchase}>Print</button>
      </div>

      {loading ? (
        <div className="barcode-print-message">Loading barcode labels...</div>
      ) : error ? (
        <div className="barcode-print-message error">{error}</div>
      ) : (
        <section className="barcode-label-sheet">
          {labels.map((labelIndex) => (
            <article className="barcode-label" key={`${purchase.purchase_id}-${labelIndex}`}>
              <BarcodeSvg value={barcodeValue} />
              <div className="barcode-line">GRN: {purchase.grn_no || '-'}</div>
              <div className="barcode-line">Part No: {purchase.part_no || '-'}</div>
              <div className="barcode-line">Location: {purchase.Location || '-'}</div>
              <div className="barcode-line">{encodePrice(purchase.unit_price, 'cost')}</div>
              <div className="barcode-line">{encodePrice(purchase.Sale_Price, 'sale')}</div>
            </article>
          ))}
        </section>
      )}
    </main>
  );
}

export default PurchaseBarcodePrint;
